import type { ReactNode } from 'react'
import { Check, ChevronDown, Lock, Users } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { cn } from '../lib/utils'
import { AUDIENCE_FRIENDS as FRIENDS, type AudienceSelection } from '../hooks/useAudienceSelection'
import type { Group } from '../types'

/**
 * Popover listing Friends + each group as toggleable channels. Callers own the
 * copy. `onlyMe` adds an "only me" row above the channels: while it's active the
 * channels read unchecked, and picking one leaves only-me with just that channel.
 */
export function AudiencePicker({
  groups,
  selection,
  triggerLabel,
  header,
  friendsLabel,
  onlyMe,
}: {
  groups: Group[]
  selection: AudienceSelection
  triggerLabel: ReactNode
  header: ReactNode
  friendsLabel: string
  onlyMe?: { active: boolean; label: string; onChange: (active: boolean) => void }
}) {
  const isSelected = (id: string) => !onlyMe?.active && selection.selected.has(id)
  const toggle = (id: string) => {
    if (onlyMe?.active) {
      selection.selectOnly(id)
      onlyMe.onChange(false)
    } else {
      selection.toggle(id)
    }
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs text-muted-foreground hover:bg-muted/50 cursor-pointer"
        >
          <Users className="size-3.5" />
          <span className="max-w-[8rem] truncate">{triggerLabel}</span>
          <ChevronDown className="size-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56">
        <p className="px-1 pb-1 text-xs font-medium text-muted-foreground">{header}</p>
        {onlyMe && (
          <>
            <ChannelRow
              label={onlyMe.label}
              icon={<Lock className="size-3.5 text-muted-foreground" />}
              selected={onlyMe.active}
              onToggle={() => onlyMe.onChange(true)}
            />
            <div className="my-1 h-px bg-border" />
          </>
        )}
        <ChannelRow label={friendsLabel} selected={isSelected(FRIENDS)} onToggle={() => toggle(FRIENDS)} />
        {groups.map(g => (
          <ChannelRow key={g.id} label={g.name} selected={isSelected(g.id)} onToggle={() => toggle(g.id)} />
        ))}
      </PopoverContent>
    </Popover>
  )
}

function ChannelRow({
  label,
  icon,
  selected,
  onToggle,
}: {
  label: string
  icon?: ReactNode
  selected: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/50 cursor-pointer"
    >
      <span className="flex items-center gap-1.5 truncate">
        {icon}
        {label}
      </span>
      <Check className={cn('size-4 shrink-0 text-primary', selected ? 'opacity-100' : 'opacity-0')} />
    </button>
  )
}
